import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, ChevronRight, ChevronLeft, RefreshCw, Target, TrendingUp, Activity, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Questions organized by section
const sections = [
  {
    id: "morning_energy",
    title: "Morning & Energy Rhythm",
    questions: [
      { id: "q1", text: "I wake easily and feel reasonably refreshed." },
      { id: "q2", text: "My energy is fairly steady through the morning." },
      { id: "q3", text: "I don't rely heavily on caffeine to feel functional." },
      { id: "q4", text: "My body feels willing to move when I wake." },
    ]
  },
  {
    id: "body_comfort",
    title: "Body Comfort & Movement",
    questions: [
      { id: "q5", text: "I move through my day without persistent pain or stiffness." },
      { id: "q6", text: "When discomfort shows up, it feels manageable rather than overwhelming." },
      { id: "q7", text: "My body recovers well after physical or emotional strain." },
      { id: "q8", text: "I feel coordinated and connected when I move." },
    ]
  },
  {
    id: "nervous_system",
    title: "Nervous System & Stress Load",
    questions: [
      { id: "q9", text: "I can notice stress without immediately bracing or shutting down." },
      { id: "q10", text: "I recover fairly quickly after stressful moments." },
      { id: "q11", text: "My breathing is calm and accessible most of the time." },
      { id: "q12", text: "I don't feel stuck in fight, flight, or freeze on a regular basis." },
    ]
  },
  {
    id: "digestion",
    title: "Digestion, Detox & Internal Flow",
    questions: [
      { id: "q13", text: "I feel comfortable and settled after most meals." },
      { id: "q14", text: "My digestion feels predictable rather than reactive." },
      { id: "q15", text: "I eliminate regularly and without strain." },
      { id: "q16", text: "My body feels like it clears things out efficiently." },
      { id: "q17", text: "I tolerate everyday sensory input (light, sound, smells) without feeling overwhelmed." },
      { id: "q18", text: "I tend to sleep through the early-morning hours without frequent waking between 3–5am." },
    ]
  },
  {
    id: "hydration",
    title: "Hydration & Tissue Quality",
    questions: [
      { id: "q19", text: "I drink enough fluids throughout the day." },
      { id: "q20", text: "I rarely feel dry, heavy, or sluggish internally." },
      { id: "q21", text: "My tissues feel pliable rather than tight or \"stuck.\"" },
      { id: "q22", text: "I recover better when I hydrate well." },
    ]
  },
  {
    id: "mood_focus",
    title: "Mood, Focus & Motivation",
    questions: [
      { id: "q23", text: "My mood feels relatively stable day to day." },
      { id: "q24", text: "I can focus without excessive effort." },
      { id: "q25", text: "I feel motivated without constantly pushing myself." },
      { id: "q26", text: "I don't live in a chronic \"I know better but don't do it\" loop." },
    ]
  },
  {
    id: "rest_sleep",
    title: "Rest, Sleep & Recovery",
    questions: [
      { id: "q27", text: "I fall asleep without much struggle." },
      { id: "q28", text: "I stay asleep for most of the night." },
      { id: "q29", text: "My sleep feels restorative." },
      { id: "q30", text: "Rest feels supportive, not stressful or lazy." },
      { id: "q31", text: "I keep relatively regular sleep hours (roughly 10pm–6am)." },
      { id: "q32", text: "I usually sleep through the night without waking due to bladder urgency." },
    ]
  }
];

const scoreCategories = {
  regulated_resilient: {
    title: "Regulated & Resilient",
    range: "32–56",
    icon: Sparkles,
    color: "emerald",
    description: "Your system has capacity and flexibility. This month is about refinement, ease, and letting the fascia respond rather than forcing change."
  },
  functional_compensating: {
    title: "Functional but Compensating",
    range: "57–88",
    icon: TrendingUp,
    color: "amber",
    description: "You're doing okay, but effort is doing some heavy lifting. Fascial maneuvers may reveal where you've been quietly holding things together."
  },
  adapted_overloaded: {
    title: "Adapted & Overloaded",
    range: "89–120",
    icon: Activity,
    color: "orange",
    description: "Your body has adapted well, but at a cost. Expect noticeable shifts as restrictions unwind and internal flow improves."
  },
  depleted_disconnected: {
    title: "Depleted or Disconnected",
    range: "121–160",
    icon: Target,
    color: "rose",
    description: "Your system may be running on reserve. Small, consistent inputs are likely to create meaningful change over the 28 days."
  }
};

const sectionNames = {
  morning_energy: "Morning & Energy Rhythm",
  body_comfort: "Body Comfort & Movement",
  nervous_system: "Nervous System & Stress Load",
  digestion: "Digestion, Detox & Internal Flow",
  hydration: "Hydration & Tissue Quality",
  mood_focus: "Mood, Focus & Motivation",
  rest_sleep: "Rest, Sleep & Recovery"
};

const WellnessSnapshotQuestionnaire = ({ userId, snapshotType, onComplete, existingSnapshot }) => {
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(existingSnapshot || null);
  const [showQuestionnaire, setShowQuestionnaire] = useState(!existingSnapshot);

  const totalQuestions = sections.reduce((acc, s) => acc + s.questions.length, 0);
  const answeredQuestions = Object.keys(responses).length;
  const progress = (answeredQuestions / totalQuestions) * 100;

  const currentSection = sections[currentSectionIndex];
  const allCurrentSectionAnswered = currentSection?.questions.every(q => responses[q.id]);
  const allQuestionsAnswered = answeredQuestions === totalQuestions;

  const handleAnswer = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNext = () => {
    if (currentSectionIndex < sections.length - 1) {
      setCurrentSectionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!allQuestionsAnswered) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/wellness-snapshot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          snapshot_type: snapshotType,
          responses: responses
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save snapshot');
      }

      const data = await response.json();
      setResult(data);
      setShowQuestionnaire(false);
      if (onComplete) {
        onComplete(data);
      }
    } catch (error) {
      console.error('Error saving snapshot:', error);
      alert('Failed to save your results. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetake = () => {
    setResponses({});
    setCurrentSectionIndex(0);
    setResult(null);
    setShowQuestionnaire(true);
  };

  // Show results if we have them
  if (result && !showQuestionnaire) {
    const category = scoreCategories[result.score_category];
    const CategoryIcon = category?.icon || Target;
    const colorClasses = {
      emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: 'text-emerald-500' },
      amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: 'text-amber-500' },
      orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', icon: 'text-orange-500' },
      rose: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', icon: 'text-rose-500' }
    };
    const colors = colorClasses[category?.color] || colorClasses.amber;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-3xl mx-auto"
      >
        {/* Results Header */}
        <div className="text-center mb-8">
          <div className={`w-20 h-20 ${colors.bg} rounded-full flex items-center justify-center mx-auto mb-4`}>
            <CategoryIcon size={40} className={colors.icon} />
          </div>
          <h2 className="font-heading text-3xl text-forest mb-2">Your Wellness Snapshot</h2>
          <p className="text-stone-500 font-body">
            {snapshotType === 'initial' ? 'Your starting point baseline' : 'Your end-of-program results'}
          </p>
        </div>

        {/* Score Card */}
        <div className={`${colors.bg} ${colors.border} border-2 rounded-2xl p-8 mb-8`}>
          <div className="text-center">
            <p className="text-stone-500 font-body text-sm mb-2">Total Score</p>
            <p className="font-heading text-5xl text-forest mb-2">{result.total_score}</p>
            <p className="text-stone-400 font-body text-sm">out of 160</p>
          </div>
          <div className="mt-6 pt-6 border-t border-stone-200">
            <h3 className={`font-heading text-2xl ${colors.text} text-center mb-3`}>
              {category?.title}
            </h3>
            <p className="text-stone-600 font-body text-center leading-relaxed">
              {category?.description}
            </p>
          </div>
        </div>

        {/* Primary Focus Zone */}
        {result.highest_section && (
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-stone-100 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-terracotta/10 rounded-full flex items-center justify-center">
                <Target size={20} className="text-terracotta" />
              </div>
              <h3 className="font-heading text-xl text-forest">Your Primary Focus Zone</h3>
            </div>
            <p className="text-stone-600 font-body mb-4">
              Based on your responses, your body may benefit most from attention in:
            </p>
            <div className="bg-terracotta/10 rounded-xl p-4">
              <p className="font-heading text-lg text-terracotta text-center">
                {sectionNames[result.highest_section]}
              </p>
            </div>
          </div>
        )}

        {/* Section Breakdown */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-stone-100 mb-8">
          <h3 className="font-heading text-xl text-forest mb-6">Section Breakdown</h3>
          <div className="space-y-4">
            {Object.entries(result.section_scores || {}).map(([sectionId, score]) => {
              const maxScore = sections.find(s => s.id === sectionId)?.questions.length * 5 || 20;
              const percentage = (score / 5) * 100;
              const isHighest = sectionId === result.highest_section;
              
              return (
                <div key={sectionId} className={`p-4 rounded-xl ${isHighest ? 'bg-terracotta/5 border border-terracotta/20' : 'bg-stone-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`font-body ${isHighest ? 'font-medium text-terracotta' : 'text-stone-700'}`}>
                      {sectionNames[sectionId]}
                      {isHighest && <span className="ml-2 text-xs bg-terracotta/20 text-terracotta px-2 py-0.5 rounded-full">Focus Area</span>}
                    </span>
                    <span className="font-body text-sm text-stone-500">{score.toFixed(1)} avg</span>
                  </div>
                  <div className="w-full h-2 bg-stone-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${isHighest ? 'bg-terracotta' : 'bg-forest/60'}`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Retake Button */}
        <div className="text-center">
          <button
            onClick={handleRetake}
            className="inline-flex items-center gap-2 text-stone-500 hover:text-forest transition-colors font-body"
          >
            <RefreshCw size={18} />
            Retake Assessment
          </button>
        </div>
      </motion.div>
    );
  }

  // Show questionnaire
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-3xl mx-auto"
    >
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="font-heading text-3xl text-forest mb-2">Wellness Snapshot</h2>
        <p className="text-stone-600 font-body">A simple snapshot of how your system is doing right now</p>
      </div>

      {/* Instructions */}
      <div className="bg-stone-50 rounded-xl p-6 mb-8">
        <h3 className="font-heading text-lg text-forest mb-3">How to Answer</h3>
        <p className="text-stone-600 font-body mb-4">
          Choose the number that best reflects your most consistent experience over the past 7–10 days.
        </p>
        <div className="grid grid-cols-5 gap-2 text-center text-sm">
          <div className="bg-white rounded-lg p-2">
            <span className="font-heading text-lg text-forest">1</span>
            <p className="text-stone-500 font-body text-xs">Consistently true</p>
          </div>
          <div className="bg-white rounded-lg p-2">
            <span className="font-heading text-lg text-forest">2</span>
            <p className="text-stone-500 font-body text-xs">Mostly true</p>
          </div>
          <div className="bg-white rounded-lg p-2">
            <span className="font-heading text-lg text-forest">3</span>
            <p className="text-stone-500 font-body text-xs">Sometimes true</p>
          </div>
          <div className="bg-white rounded-lg p-2">
            <span className="font-heading text-lg text-forest">4</span>
            <p className="text-stone-500 font-body text-xs">Rarely true</p>
          </div>
          <div className="bg-white rounded-lg p-2">
            <span className="font-heading text-lg text-forest">5</span>
            <p className="text-stone-500 font-body text-xs">Not true at all</p>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-stone-500 font-body text-sm">Progress</span>
          <span className="text-stone-500 font-body text-sm">{answeredQuestions} / {totalQuestions} questions</span>
        </div>
        <div className="w-full h-2 bg-stone-200 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-terracotta rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      {/* Section Navigation */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
        {sections.map((section, index) => {
          const sectionAnswered = section.questions.every(q => responses[q.id]);
          return (
            <button
              key={section.id}
              onClick={() => setCurrentSectionIndex(index)}
              className={`flex-shrink-0 px-4 py-2 rounded-full font-body text-sm transition-all ${
                index === currentSectionIndex
                  ? 'bg-forest text-cream'
                  : sectionAnswered
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
              }`}
            >
              {sectionAnswered && index !== currentSectionIndex && (
                <CheckCircle size={14} className="inline mr-1" />
              )}
              {index + 1}
            </button>
          );
        })}
      </div>

      {/* Current Section */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSection.id}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-2xl p-8 shadow-lg border border-stone-100 mb-8"
        >
          <h3 className="font-heading text-xl text-forest mb-6">
            <span className="text-terracotta mr-2">{currentSectionIndex + 1}.</span>
            {currentSection.title}
          </h3>

          <div className="space-y-6">
            {currentSection.questions.map((question, qIndex) => (
              <div key={question.id} className="border-b border-stone-100 last:border-0 pb-6 last:pb-0">
                <p className="text-stone-700 font-body mb-4">
                  {question.text}
                </p>
                <div className="flex items-center justify-center gap-4">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <label
                      key={value}
                      className="cursor-pointer group"
                    >
                      <input
                        type="radio"
                        name={question.id}
                        value={value}
                        checked={responses[question.id] === value}
                        onChange={() => handleAnswer(question.id, value)}
                        className="sr-only"
                      />
                      <div
                        className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all ${
                          responses[question.id] === value
                            ? 'border-terracotta bg-terracotta text-white scale-110'
                            : 'border-stone-300 text-stone-500 group-hover:border-terracotta/50 group-hover:scale-105'
                        }`}
                      >
                        <span className="font-heading text-lg">{value}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrevious}
          disabled={currentSectionIndex === 0}
          className={`flex items-center gap-2 font-body ${
            currentSectionIndex === 0
              ? 'text-stone-300 cursor-not-allowed'
              : 'text-stone-500 hover:text-forest'
          }`}
        >
          <ChevronLeft size={18} />
          Previous
        </button>

        {currentSectionIndex < sections.length - 1 ? (
          <button
            onClick={handleNext}
            disabled={!allCurrentSectionAnswered}
            className={`flex items-center gap-2 px-6 py-2 rounded-full font-body transition-all ${
              allCurrentSectionAnswered
                ? 'bg-forest text-cream hover:bg-forest-dark'
                : 'bg-stone-200 text-stone-400 cursor-not-allowed'
            }`}
          >
            Next Section
            <ChevronRight size={18} />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={!allQuestionsAnswered || isSubmitting}
            className={`flex items-center gap-2 px-8 py-3 rounded-full font-body font-medium transition-all ${
              allQuestionsAnswered && !isSubmitting
                ? 'bg-terracotta text-white hover:bg-terracotta-dark'
                : 'bg-stone-200 text-stone-400 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? (
              <>
                <RefreshCw size={18} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                Submit & See Results
                <CheckCircle size={18} />
              </>
            )}
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default WellnessSnapshotQuestionnaire;
